import React, { useState, useMemo } from 'react';
import { Box, Typography, Paper, Grid, Card, CardContent, Button, Tab, Tabs, useTheme } from '@mui/material';
import { DataGrid, GridColDef, GridValueFormatter } from '@mui/x-data-grid';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsightsIcon from '@mui/icons-material/Insights';
import { useGetIndustryBenchmarkDataQuery } from '../../gql/generated/graphql';
import ExcelUploadComponent from './components/ExcelUploadComponent';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`industry-benchmarks-tabpanel-${index}`}
      aria-labelledby={`industry-benchmarks-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const IndustryBenchmarksPage: React.FC = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const { data, loading, error, refetch } = useGetIndustryBenchmarkDataQuery();

  // Create a map of SIC codes to descriptions
  const sicCodeMap = useMemo(() => {
    const map = new Map<number, string>();
    if (data?.sicCodes) {
      data.sicCodes.forEach(sic => {
        if (sic.sicCodeInt && sic.description) {
          map.set(sic.sicCodeInt, sic.description);
        }
      });
    }
    return map;
  }, [data]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Columns for the data grid
  const columns: GridColDef[] = [
    { field: 'year', headerName: 'Year', width: 100 },
    { field: 'sic', headerName: 'SIC Code', width: 120 },
    { field: 'name', headerName: 'Company', flex: 1, minWidth: 200 },
    { field: 'region', headerName: 'Region', width: 120 },
    { field: 'country', headerName: 'Country', width: 120 },
    {
      field: 'ebitda_sales',
      headerName: 'EBITDA/Sales',
      width: 130,
      valueFormatter: ((value: number | null) =>
        value ? `${(value * 100).toFixed(1)}%` : 'N/A') as GridValueFormatter,
    },
    {
      field: 'one_year_sales_growth',
      headerName: '1Y Sales Growth',
      width: 150,
      valueFormatter: ((value: number | null) =>
        value ? `${(value * 100).toFixed(1)}%` : 'N/A') as GridValueFormatter,
    },
    {
      field: 'ev_ebitda',
      headerName: 'EV/EBITDA',
      width: 120,
      valueFormatter: ((value: number | null) => (value ? value.toFixed(1) : 'N/A')) as GridValueFormatter,
    },
    {
      field: 'z_score',
      headerName: 'Z-Score',
      width: 100,
      valueFormatter: ((value: number | null) => (value ? value.toFixed(2) : 'N/A')) as GridValueFormatter,
    },
  ];

  // Process data for the grid
  const rows = useMemo(() => {
    if (!data?.industryBenchmarkData) return [];
    return data.industryBenchmarkData.map((item: any) => ({
      id: item.id,
      year: item.year,
      sic: item.sic,
      name: item.name || 'N/A',
      region: item.region || 'N/A',
      country: item.country || 'N/A',
      ebitda_sales: item.ebitdaSales,
      one_year_sales_growth: item.oneYearSalesGrowth,
      ev_ebitda: item.evEbitda,
      z_score: item.zScore,
    }));
  }, [data]);

  // Calculate statistics
  const statistics = useMemo(() => {
    if (!data?.industryBenchmarkData) return null;

    const yearStats = new Map<number, number>();
    const sicStats = new Map<number, number>();
    const regionStats = new Map<string, number>();
    const yearGrowthData = new Map<number, { sum: number; count: number }>();
    const sicGrowthData = new Map<number, { sum: number; count: number; name?: string }>();

    data.industryBenchmarkData.forEach((item: any) => {
      // Year statistics
      if (item.year) {
        yearStats.set(item.year, (yearStats.get(item.year) || 0) + 1);

        // Track growth data by year
        if (item.oneYearSalesGrowth !== null && item.oneYearSalesGrowth !== undefined) {
          const current = yearGrowthData.get(item.year) || { sum: 0, count: 0 };
          yearGrowthData.set(item.year, {
            sum: current.sum + item.oneYearSalesGrowth,
            count: current.count + 1,
          });
        }
      }
      // SIC statistics
      if (item.sic) {
        sicStats.set(item.sic, (sicStats.get(item.sic) || 0) + 1);

        // Track growth data by SIC
        if (item.oneYearSalesGrowth !== null && item.oneYearSalesGrowth !== undefined) {
          const current = sicGrowthData.get(item.sic) || { sum: 0, count: 0, name: item.name };
          sicGrowthData.set(item.sic, {
            sum: current.sum + item.oneYearSalesGrowth,
            count: current.count + 1,
            name: current.name || item.name,
          });
        }
      }
      // Region statistics
      if (item.region) {
        regionStats.set(item.region, (regionStats.get(item.region) || 0) + 1);
      }
    });

    // Calculate average growth by year
    const avgGrowthByYear = Array.from(yearGrowthData.entries())
      .map(([year, data]) => ({
        year,
        avgGrowth: data.sum / data.count,
        count: data.count,
      }))
      .sort((a, b) => b.year - a.year);

    // Calculate average growth by SIC
    const avgGrowthBySic = Array.from(sicGrowthData.entries())
      .map(([sic, data]) => ({
        sic,
        avgGrowth: data.sum / data.count,
        count: data.count,
        name: data.name,
      }))
      .filter(item => item.count >= 3) // Only include SICs with at least 3 data points
      .sort((a, b) => b.avgGrowth - a.avgGrowth);

    const allSicStats = Array.from(sicStats.entries()).sort((a, b) => b[1] - a[1]);

    return {
      totalRecords: data.industryBenchmarkData.length,
      yearStats: Array.from(yearStats.entries()).sort((a, b) => b[0] - a[0]),
      sicStats: allSicStats.slice(0, 6), // Reduced from 10 to 6
      totalSicCount: sicStats.size,
      regionStats: Array.from(regionStats.entries()).sort((a, b) => b[1] - a[1]),
      avgGrowthByYear,
      topGrowingSics: avgGrowthBySic.slice(0, 6),
    };
  }, [data]);

  if (loading) return <Box>Loading...</Box>;
  if (error) return <Box>Error loading data: {error.message}</Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InsightsIcon fontSize="large" />
          Industry Benchmarks
        </Typography>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={() => setTabValue(2)}
          sx={{
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          Upload Data
        </Button>
      </Box>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="industry benchmarks tabs">
          <Tab label="Overview" />
          <Tab label="Data Analysis" />
          <Tab label="Upload Excel" />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Records
                </Typography>
                <Typography variant="h4">{statistics?.totalRecords || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Years Covered
                </Typography>
                <Typography variant="h4">{statistics?.yearStats.length || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Industries (SIC)
                </Typography>
                <Typography variant="h4">{statistics?.totalSicCount || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Regions
                </Typography>
                <Typography variant="h4">{statistics?.regionStats.length || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Average Sales Growth by Year
          </Typography>
          <Grid container spacing={2}>
            {statistics?.avgGrowthByYear.slice(0, 6).map(({ year, avgGrowth, count }) => (
              <Grid item xs={6} sm={4} md={2} key={year}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h6">{year}</Typography>
                  <Typography variant="h5" color={avgGrowth >= 0 ? 'success.main' : 'error.main'}>
                    {(avgGrowth * 100).toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {count} companies
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Top Industries by Data Points
          </Typography>
          <Grid container spacing={2}>
            {statistics?.sicStats.map(([sic, count]) => (
              <Grid item xs={12} sm={6} md={4} key={sic}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                    {sicCodeMap.get(sic) || `SIC ${sic}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    SIC {sic}
                  </Typography>
                  <Typography color="text.secondary">{count} records</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Highest Growing Industries
          </Typography>
          <Grid container spacing={2}>
            {statistics?.topGrowingSics.map(({ sic, avgGrowth, count, name }) => (
              <Grid item xs={12} sm={6} md={4} key={sic}>
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <Box sx={{ flex: 1, mr: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                        {sicCodeMap.get(sic) || `SIC ${sic}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        SIC {sic} • {count} companies
                      </Typography>
                    </Box>
                    <Typography variant="h5" color={avgGrowth >= 0 ? 'success.main' : 'error.main'}>
                      {(avgGrowth * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid rows={rows} columns={columns} pageSizeOptions={[10, 25, 50, 100]} disableRowSelectionOnClick />
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Upload Excel File
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Upload an Excel file containing industry benchmark data. The system will guide you through mapping the columns
          to the database fields.
        </Typography>
        <ExcelUploadComponent
          onUploadComplete={async data => {
            console.log('Upload completed with data:', data);
            // Refresh the data after successful upload
            await refetch();
            // Switch to the overview tab to see the new data
            setTabValue(0);
          }}
        />
      </TabPanel>
    </Box>
  );
};

export default IndustryBenchmarksPage;
